import {
  displayedDelta,
  formatCount,
  formatKnowableAt,
  formatKnowableAtExact,
  formatScoreDelta,
  listOf,
} from './format';
import { availableOptionalColumns, OPTIONAL_COLUMN_KEYS } from './columns';
import { isSpecFeed } from './specForFeed';
import { INPUT_STATUSES } from '@services/feedView';

// Everything the Feed Viewer says out loud.
//
// It was in three shapes across two files: module constants, builder functions, and bare literals
// inside JSX — and it is simultaneously what the test suite anchors on, some five hundred
// assertions of it. So rewording a sentence meant hunting render bodies, and turning the suite red
// at a volume where a real regression is indistinguishable from expected churn.
//
// One file, so a reword is one diff and the tests import what the app renders rather than retyping
// it. Nothing here is new: every string is verbatim from where it lived.
//
// WHAT BELONGS HERE: sentences and labels an analyst reads. What does NOT: format.js's value
// formatters, which turn a number into a string for a cell rather than telling the reader anything;
// and the accessible names built from a column's own label, which belong to the column model.
//
// The hover twins moved in from format.js on purpose. previousScoreTitle and scoreDeltaTitle draw
// the same distinctions the notes above the table draw — "no earlier occasion came back" against
// "one came back and scored nothing" — and they lived in a different file with a different test, so
// the two could disagree and nothing would notice. They are now neighbours.

// ---------------------------------------------------------------------------------------------
// Above the table
// ---------------------------------------------------------------------------------------------

export const MOCK_NOTICE_HEADING = 'Sample data — this did not come from the API.';

export const mockNoticeDetail = ({ isSynthetic, feedId }) =>
  isSynthetic
    ? `A hand-written ${feedId} response — illustrative values, for local development.`
    : `A captured ${feedId} response, for local development.`;

export const MOCK_NOTICE_ACTION = 'Turn off “Use sample feed” to run a real query.';

export const SUPERSEDED_HEADING = 'Showing the previous run while a newer one loads.';
export const SUPERSEDED_DETAIL = 'These numbers answer the query you ran before this one.';

// ---------------------------------------------------------------------------------------------
// Empty states
// ---------------------------------------------------------------------------------------------

export const NO_ROWS_HEADING = 'No rows for this feed and window.';

// What an empty response can mean — for a feed that composes members, and for one that doesn't.
//
// The window is KNOWABLE time, not publication time: a row becomes knowable at its source stamp plus
// a modelled latency (an hour for most document types, nine for an earnings call), so a window ending
// today systematically excludes everything published inside that latency. A recent, correct,
// completely empty result is the ordinary outcome of a short preset, and nothing on the page said so
// — which left "widen the window" as advice the analyst had no reason to believe.
//
// The combined-feed line is gated on the feed NOT resolving to a score spec, because an empty
// response has no rows to read the feed's shape off — that is the whole point of this branch. It is
// hedged rather than asserted for the one case the id cannot settle: since PR #459 a score may
// publish under a free-form `feed_name` carrying no spec id, and `isSpecFeed` fails closed on those.
//
// It does not mention alignment. On this endpoint a misaligned occasion still returns its row, with
// the members that missed it marked and no score — ALL_UNSCORED_NOTE's case, not this one. An empty
// grid means no member scored anything here at all.
export const emptyResultCauses = (feedId) => {
  const causes = [
    'The window is knowable time — a row becomes knowable at its source stamp plus a modelled latency, so a window ending today can legitimately return nothing.',
  ];
  if (!isSpecFeed(feedId)) {
    causes.push(
      'If this feed combines member feeds, it has rows only where a member scored something — a window in which no member scored comes back empty rather than unscored.'
    );
  }
  causes.push('Widen the knowable window, or clear the company filter, and view the feed again.');
  return causes;
};

export const NO_MATCHING_ROWS_HEADING = 'No loaded row matches these filters.';

export const clearFiltersHint = (totalCount) =>
  `Clear the filter to see all ${formatCount(totalCount)} returned rows again.`;

// ---------------------------------------------------------------------------------------------
// The counter row
// ---------------------------------------------------------------------------------------------

// The unit changes with the toggle, so the word does too — and the occasions behind the average are
// named, because "12 companies" alone hides that 400 rows went into it.
export const rowCountLabel = (rowCount) => `${formatCount(rowCount)} row${rowCount === 1 ? '' : 's'}`;

export const aggregateCountLabel = ({ companies, occurrences }) =>
  `${formatCount(companies)} ${companies === 1 ? 'company' : 'companies'} · averaged from ${formatCount(
    occurrences
  )} occasion${occurrences === 1 ? '' : 's'}`;

export const SCORE_ONLY_SUFFIX = ' · score-only';

export const coveredSpanSuffix = (covered) =>
  ` · covering ${formatKnowableAt(covered.from)} → ${formatKnowableAt(covered.to)}`;

export const shownOfTotalLabel = (shown, total) => `Showing ${formatCount(shown)} of ${formatCount(total)}`;

// ---------------------------------------------------------------------------------------------
// The notes under the table
// ---------------------------------------------------------------------------------------------

// What a break in a Movement line is. Worth saying once above the table for the reason the em dash
// needed saying: an absence drawn rather than written has no word on it, and the reading it invites —
// that the company scored nothing there, or worse, scored zero — is the one this page exists to
// refuse.
export const MOVEMENT_BREAK_NOTE =
  'A break in a Movement line is an occasion with no score. The line is never drawn through one, and never down to zero.';

// What a blank Prev score or Change says while the rows are averaged. One sentence for both columns,
// because the reason is the same and it is a property of the VIEW rather than of the row — unlike
// previousScoreTitle, whose whole job is to separate three per-row causes.
export const AGGREGATED_DERIVED_TITLE =
  'Averaged rows have no previous occasion to compare against. Clear “Average per company” to see each occasion’s change.';

// The same fact as a paragraph rather than a tooltip, for the reader who is not hovering.
export const AGGREGATED_DERIVED_NOTE =
  'Prev score and Change are empty while rows are averaged: an average spans several occasions, so it has no single one to have come after. Movement is what is left to read — it draws every occasion behind the average, over the whole read rather than the filtered view.';

// What an em dash in the Score column means, which depends on whether this feed composes anything.
//
// Stale is its own branch because the server counts a carried-forward value as missing, so only the
// per-member status can separate them — and "missing" over a feed whose members all arrived, unfresh,
// is a false statement.
//
// Slim is why this is not a two-way choice. Slim drops `inputs` from every row, so a multi-input
// feed read slim arrives with no member columns either — but there the members do exist and simply
// weren't sent, and denying them would be the same false statement pointing the other way. A slim
// read therefore gets the sentence that describes the em dash and claims nothing about its cause.
export const missingScoreNote = ({ hasMemberColumns, isSlim, anyStale }) => {
  if (hasMemberColumns) {
    return anyStale
      ? 'A row scored — is missing a member’s value for that occasion, or carrying one forward that isn’t fresh for it. That is a gap, not a score of zero.'
      : 'A row scored — is missing at least one member’s value for that occasion. That is a gap, not a score of zero.';
  }
  if (isSlim) return 'A row scored — has no score for that occasion. That is a gap, not a score of zero.';
  return 'A row scored — has no score for that occasion. There are no inputs here to have gone missing; the score itself is absent. That is a gap, not a score of zero.';
};

// What a blank Prev score or Change means for THIS read — the thing the em dash cannot say and the
// tooltip only says to a mouse.
//
// Two readings have to be kept apart, and only the request can separate them. `attachPreviousScores`
// walks the rows this response returned, so a row with no predecessor means "none came back". Over an
// UNBOUNDED read — no knowable-from, which is what the form starts with — the server was asked for
// every occasion, so none came back because none exists. Over a bounded one the window is the more
// likely cause, and it is the one the analyst can do something about.
//
// It names its own columns, deliberately. missingScoreNote sits directly above and also explains an
// em dash; two unattributed paragraphs about three columns of em dashes read as one sentence about
// the wrong column.
export const previousScoreNote = ({ allWithoutPrevious, anyWithoutPrevious, anyUnscoredPrevious, knowableFloor }) => {
  if (allWithoutPrevious) {
    return knowableFloor
      ? 'No company in this read has an earlier occasion, so Prev score and Change are empty throughout. Widen the knowable window to bring the previous occasion in.'
      : 'No company in this read has an earlier occasion, so Prev score and Change are empty throughout. This read was not bounded below, so these are the only occasions the feed holds for these companies.';
  }
  if (anyWithoutPrevious && anyUnscoredPrevious) {
    return 'A — in Prev score or Change has two causes on this table: this read holds no earlier occasion for that company, or it holds one that scored nothing. Hover a cell to see which, and when.';
  }
  if (anyUnscoredPrevious) {
    return 'A — in Prev score or Change means that company’s previous occasion scored nothing. The occasion is in this read; the score is not. Hover a cell for its date.';
  }
  return 'A — in Prev score or Change means this read holds no earlier occasion for that company, not that the company was unscored then. Hover a cell for the occasion it came from.';
};

// Why a combined feed can return a full table in which NOTHING is scored — the one cause an analyst
// cannot guess from the grid, and the one the engine reports with no error and no warning.
//
// A combined feed scores an occasion only where every member lands on the SAME (company, knowable-at)
// pair, to the second: each member's availability is an exact timestamp hit, so the union grid plus
// require-all collapses to an inner join across all of them. Two members that scored the same
// document can still miss each other — a member declaring only 10-K carries an hour of modelled
// latency where a sibling declaring an earnings call carries nine, and a member pulling extra
// context stamps later than one that didn't. Neither is an error; the rows simply never meet.
//
// A member with no rows at all does the same thing from the other direction: it cannot shrink the
// grid, so every row keeps its slot and every row loses that member.
//
// Deliberately NOT shown for a partly-scored table. There, some occasions did align, so alignment is
// not what explains the ones that didn't, and leading with it would send the analyst after the wrong
// thing. Read off the whole response rather than the filtered view for the same reason the flag is:
// the missing-score toggle makes every visible row unscored by construction.
export const ALL_UNSCORED_NOTE =
  'Nothing in this response is scored. A combined feed scores an occasion only where every member lands on the same knowable-at, to the second — so a member whose stamp differs (a different document-type latency, or a wider context window), or one with no rows at all, leaves every row a gap. Drill into a member to see which ones arrived.';

// ---------------------------------------------------------------------------------------------
// The hover twins — the per-row half of the two notes above
// ---------------------------------------------------------------------------------------------

// What a Prev score cell says when hovered — and, for three of its five states, the ONLY place the
// difference between them is stated. The cell itself is an em dash in all three.
//
// The wording is scoped to the READ on every branch that cannot prove more, because that is all the
// client can see: `attachPreviousScores` walks the rows this response returned, so a row with no
// predecessor means "none came back", not "none exists". The one exception is an UNBOUNDED read —
// `knowableFloor === null`, which the default form state produces — where the server was asked for
// every occasion and answering with one is evidence about the feed itself.
//
// Getting this backwards is the failure that matters: an analyst told a company has no earlier score,
// when the truth is the window did not reach it, concludes something false about the company.
export const previousScoreTitle = (row, { knowableFloor = null } = {}) => {
  if (!row) return null;
  if (row.companyId === null || row.companyId === undefined) {
    return 'This row has no company, so it has no earlier score.';
  }
  if (!row.prevScoreKnowableAt) {
    return knowableFloor
      ? 'No earlier occasion for this company in this read — widen the knowable window to look further back.'
      : 'No earlier occasion for this company in this feed.';
  }
  const at = formatKnowableAtExact(row.prevScoreKnowableAt);
  return typeof row.prevScore === 'number' && isFinite(row.prevScore)
    ? `Its score at ${at}, the previous occasion in this read.`
    : `Unscored at ${at}, the previous occasion in this read.`;
};

// What a Change cell says when hovered. Two jobs beyond naming the occasion.
//
// A zero is the one that needs words. When every input on a row is STALE the score was recomputed
// from the same member values as the earlier occasion, so the change is exactly 0 — and two identical
// numbers side by side read as "the world did not move" when the truth is "no new information
// arrived". The markers one column over already draw that distinction; this sentence keeps the
// Change column from contradicting them.
export const scoreDeltaTitle = (row) => {
  if (!row) return null;
  // Read through displayedDelta rather than off the raw number, so this sentence describes the digits
  // the cell is actually showing. A change of 9.99e-6 prints as '0', and branching on the raw value
  // here would caption that cell 'moved' while it reads 'unchanged' — and would skip the
  // carried-forward warning below, which is the whole reason the zero branch exists.
  const shown = displayedDelta(row.scoreDelta);
  if (shown === null) {
    // No change to describe. Prev score's own tooltip already says why, and repeating it here would
    // put two explanations of one gap on one row.
    return null;
  }
  const at = formatKnowableAtExact(row.prevScoreKnowableAt);
  const carried =
    (row.members || []).length > 0 && (row.members || []).every((input) => input.status === INPUT_STATUSES.STALE);
  if (shown === 0) {
    return carried
      ? `Unchanged since ${at} — but every input on this row was carried forward, so nothing new was scored.`
      : `Unchanged since ${at}.`;
  }
  return `${formatScoreDelta(row.scoreDelta)} against its score at ${at}.`;
};

// ---------------------------------------------------------------------------------------------
// The page's column-refusal notice
// ---------------------------------------------------------------------------------------------

// The enrichment columns as the notice says them out loud, rather than as the chooser labels them:
// "Indices" is a column header, "index membership" is a sentence.
export const NOTICE_NAMES = {
  sector: 'sector',
  market_cap: 'market cap',
  memberships: 'index membership',
};

export const capitalizeFirst = (text) => text.charAt(0).toUpperCase() + text.slice(1);

// What the notice can truthfully claim depends on what actually arrived, so it is read off the rows
// rather than written out flat. A server that rejects the `columns` KEY while still returning its
// full row shape by default — which is exactly what the only real capture of this endpoint does —
// sends back rows that DO carry sector. The flat wording then printed "Sector … aren't in it"
// directly above a table drawing a Sector column, which is a worse failure than saying nothing.
export const droppedColumnsNotice = (result, refusedNames) => {
  const loaded = new Set(
    availableOptionalColumns(result)
      .filter((column) => column.loaded)
      .map((column) => column.key)
  );
  const arrived = OPTIONAL_COLUMN_KEYS.filter((key) => loaded.has(key)).map((key) => NOTICE_NAMES[key]);
  const missing = OPTIONAL_COLUMN_KEYS.filter((key) => !loaded.has(key)).map((key) => NOTICE_NAMES[key]);

  const floor = 'knowable-at, company, ticker and score';
  const got = arrived.length ? `${floor}, plus ${listOf(arrived)}` : floor;
  const lost = missing.length
    ? `${capitalizeFirst(listOf(missing))} ${missing.length === 1 ? 'isn’t' : 'aren’t'} in it.`
    : 'Nothing was lost with it — this feed returns the enrichment unasked.';

  // Named in the WIRE spelling rather than through NOTICE_NAMES, deliberately. This half of the
  // sentence is a diagnosis, not a description: the name is the one the request sent and the one the
  // server echoed back, so it is what an analyst matches against a spec, a log line or the feed's
  // own `available_columns`. "market cap" reads better and matches nothing.
  const blamed = refusedNames.length ? ` — it named ${listOf(refusedNames)} —` : ',';

  return `This feed wouldn’t take the column request${blamed} so what’s below is whatever it returns without one: ${got}. ${lost}`;
};

// ---------------------------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------------------------

// Named for the gap it closes. "Add Reasoning" wins when the payload is missing, because that
// re-read brings the switched-on columns back with it anyway — one press, and the label promises the
// thing the analyst cannot otherwise get.
export const ADD_REASONING_LABEL = 'Add Reasoning';

export const loadColumnsLabel = (columnLabels) => `Load ${listOf(columnLabels)}`;

export const ASK_FOR_COLUMNS_AGAIN_LABEL = 'Read it again asking for the columns';

export const BACK_CLEAR_LABEL = 'Clear the feed and start over';
export const BACK_HOME_LABEL = 'Back to home';

// A re-read is acknowledged differently from a first read — the table below is still the old one, so
// "again" is the word that stops it reading as a page that has not moved. A UX decision, named here
// rather than left in a ternary inside the status row.
export const RUNNING_FIRST_READ = 'Materializing the feed…';
export const RUNNING_RE_READ = 'Materializing the feed again…';
export const runningLabel = (hasResult) => (hasResult ? RUNNING_RE_READ : RUNNING_FIRST_READ);
